import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { IMAGES } from "../lib/brand";
import { api, apiError, track, trackBeacon } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const OUTCOMES = [
  { t: "What Estate Management actually is", d: "Know what the role is, how it differs from House Manager and PA, who hires for it — and why discretion is the price of entry." },
  { t: "The standard of private service", d: "Recognise the difference between hospitality and private service, and the small moments and presence that define the standard." },
  { t: "The traits and experience that translate", d: "See six traits as observable evidence, and name what your existing career already gives you." },
  { t: "How people actually get hired", d: "Understand how people arrive, how agencies work, how to present a CV without the title — and the mistake most make before applying." },
  { t: "What to understand before accepting a role", d: "Have the questions on fit, availability, negotiation, travel, confidentiality and culture before you say yes." },
  { t: "How to approach the first thirty days", d: "An introduction to five movements — observe, document, understand, lead, keep adjusting — for walking into a house with a plan rather than hope." },
  { t: "How to record and report professionally", d: "Know what to write, what never to write, and how a principal reads a report." },
  { t: "Marella's personal path into Estate Management", d: "The unglamorous version — and two clear paths forward depending on where you stand today." },
];

const INCLUDED = [
  { label: "The Guide", items: [{ title: "How to Start as an Estate Manager", note: "62-page guide", d: "The course itself: eight chapters, written from inside the profession, with each companion resource introduced where it becomes useful." }] },
  { label: "Professional Tools", items: [
    { title: "Household Snapshot", note: "Editable Excel workbook", d: "Linked sheets for properties, staff, vendors, utilities, appliances, preferences and more — so what you learn about a house is not stored only in your head." },
    { title: "Estate Manager Daily Log", note: "Editable Excel workbook", d: "A daily record of tasks, stakeholders, status and follow-ups — your professional memory, and the basis for any report you write." },
  ] },
  { label: "Workbook", items: [{ title: "The Art of Hospitality", note: "Private Service Self-Reflection Journal", d: "Twelve questions on presence, anticipation, quiet excellence and balance — an evening's honest look at how you already work." }] },
  { label: "Quick References", items: [
    { title: "The Five-Minute Morning", note: "One page", d: "A short walk-through to set the tone of the house before the principal notices anything." },
    { title: "10 Phrases for Polished Communication", note: "Two pages", d: "What to say instead — and why — when reporting to principals, staff and vendors." },
  ] },
  { label: "Carry Card", items: [{ title: "Invisible Excellence", note: "Printable pocket card", d: "A quiet reminder of how you want to show up — print it, cut it, carry it." }] },
];

const WHO_FOR = [
  "You are exploring Estate Management as a career.",
  "You work as a Personal Assistant, House Manager, hospitality professional, property manager or event professional.",
  "You already work inside a private household but want to understand the profession more fully.",
  "You are interested in private service but don't yet know how the industry works.",
  "You want a practical starting point rather than piecing together information from dozens of sources.",
];

const NOT = [
  "This is not a certification that promises you a job.",
  "It is not a shortcut into UHNW households.",
  "It is not a promise that you will become an Estate Manager immediately.",
  "It is a practical introduction to help you understand the profession and make more informed next steps.",
];

const FAQ = [
  { q: "Who is this course for?", a: "Anyone curious about Estate Management — including PAs, house managers, hospitality and event professionals, and those already working in private homes who want to understand the profession." },
  { q: "Do I need Estate Management experience?", a: "No. This is an introduction designed for people entering the field or considering it. Experience in a service-oriented role is helpful but not required." },
  { q: "Is this a certification?", a: "No. It is a practical introduction to the profession, not a certification or a guarantee of employment." },
  { q: "How is the course delivered?", a: "It is a self-paced online course. You work through the lessons in your own time, at your own pace." },
  { q: "How do I access it?", a: "Complete checkout with Stripe — no account needed first. If you're new, we email you a secure activation link to set your password and open your library. Existing members simply sign in." },
  { q: "What happens after I purchase?", a: "Your payment is confirmed on screen. New customers receive an activation email to open their library; existing customers sign in and find the course waiting. From then on it's yours to return to anytime." },
  { q: "How is this different from Becoming an Estate Manager™?", a: "This course is the foundation: understanding the profession, recognising your transferable experience and entering it with clarity. Becoming an Estate Manager™ is the more advanced preparation for the early stages of working inside a household." },
];

export default function CourseProduct() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get(`/courses/${courseId}`).then((r) => { setCourse(r.data); document.title = `${r.data.title} · Il Salotto Digitale`; }).catch(() => {});
  }, [courseId]);
  useEffect(() => {
    if (courseId !== "how-to-start-as-an-estate-manager") return;
    track("course_view", courseId);
    if (typeof document !== "undefined" && document.referrer && document.referrer.includes("/api/guide/download")) {
      track("guide_cta_click", courseId);
    }
  }, [courseId]);
  useEffect(() => {
    if (user) api.get("/my/courses").then((r) => setEnrolled(r.data.some((c) => c.id === courseId))).catch(() => {});
  }, [user, courseId]);

  const begin = async () => {
    if (enrolled) { nav(`/learn/${courseId}`); return; }
    if (courseId === "how-to-start-as-an-estate-manager") track("course_cta_click", courseId);
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { course_id: courseId, origin_url: window.location.origin });
      trackBeacon("checkout_begin", courseId);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const price = course ? Number(course.price).toFixed(0) : "149";
  const comingSoon = !!course && course.available === false && !enrolled;
  const CTA = ({ testid }) => (
    comingSoon ? (
      <span data-testid={testid.replace("begin", "soon")} className="inline-flex items-center gap-3 border border-ink/25 text-ink-soft px-8 py-4 text-[11px] uppercase tracking-[0.2em] cursor-default select-none">Coming Soon</span>
    ) : (
      <button onClick={begin} disabled={busy} data-testid={testid} className="group inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors disabled:opacity-60">
        {busy ? "Redirecting…" : enrolled ? "Go to the Course" : "Begin the Course"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
      </button>
    )
  );

  return (
    <div data-testid="course-product-page" className="pt-20">
      {/* HERO */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <FadeIn><MetaLabel>Il Salotto Digitale · Course</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-8 font-serif font-light text-ink text-4xl sm:text-5xl lg:text-[4.5rem] leading-[1.02] tracking-tight">How to Start as an Estate Manager</h1>
          </FadeIn>
          <FadeIn delay={0.14}>
            <p className="mt-8 font-serif italic text-xl lg:text-2xl text-ink-soft leading-snug">You know you're interested in Estate Management. You just don't know where to begin.</p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-6 text-lg font-light text-ink-soft leading-relaxed max-w-xl">Understand what Estate Management really involves, recognize the experience you already bring to the profession, and learn how to take your first steps with confidence, preparation and a clear sense of direction.</p>
          </FadeIn>
          <FadeIn delay={0.23}>
            <p className="mt-4 text-sm font-light text-ink-soft max-w-xl">The foundational introduction, with the professional tools to begin.</p>
          </FadeIn>
          <FadeIn delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <span className="font-serif text-3xl text-ink">${price}</span>
              <CTA testid="hero-begin" />
            </div>
            <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-ink-soft">Self-paced · Online access</p>
          </FadeIn>
        </div>
        <FadeIn delay={0.2} className="lg:col-span-5">
          <div className="overflow-hidden aspect-[4/5]">
            <img src={course?.cover_url || IMAGES.livingWell} alt="How to Start as an Estate Manager" className="w-full h-full object-cover" />
          </div>
        </FadeIn>
      </section>

      {/* DOES THIS SOUND FAMILIAR */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <Reveal className="lg:col-span-4"><MetaLabel>Does this sound familiar?</MetaLabel></Reveal>
          <div className="lg:col-span-8 max-w-2xl">
            <Reveal><p className="text-lg font-light text-ink-soft leading-relaxed">Maybe you've worked in hospitality, personal assistance, property management, events or another service-focused profession. You've heard about Estate Management — and something about the work feels familiar. But you're wondering:</p></Reveal>
            <Reveal delay={0.08}>
              <ul className="mt-8 space-y-4 border-t border-line pt-8">
                {["What does an Estate Manager actually do?","Am I qualified?","What skills transfer from my current career?","How do private households operate?","Where do I even begin looking for this type of work?"].map((q) => (
                  <li key={q} className="font-serif text-xl lg:text-2xl font-light text-ink">{q}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.14}><p className="mt-8 text-lg font-light text-ink-soft">This course was created to give you a clear starting point.</p></Reveal>
          </div>
        </div>
      </section>

      {/* FROM INSIDE THE PROFESSION */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-20">
        <Reveal className="lg:col-span-4"><MetaLabel>From inside the profession</MetaLabel></Reveal>
        <div className="lg:col-span-8 max-w-2xl space-y-6 text-lg font-light text-ink-soft leading-relaxed">
          <Reveal><p className="font-serif text-2xl lg:text-3xl text-ink leading-[1.3]">I didn't create this course to teach Estate Management from a textbook. I created it because this is the work I do.</p></Reveal>
          <Reveal delay={0.08}><p>I am a working Estate Manager in Miami, with more than six years in the profession. Before that, I worked in hospitality management, international medical congresses and events, and interior design — and I draw on all of it, every day, inside private residences.</p></Reveal>
          <Reveal delay={0.1}><p>Estate Management is a profession that is often difficult to understand from the outside. Much of the work happens quietly, behind the scenes, and there is no single traditional path into it.</p></Reveal>
          <Reveal delay={0.12}><p>This course is the introduction I wish I had been given when I first discovered the profession — written from practice, not theory.</p></Reveal>
          <Reveal delay={0.16}><p className="font-serif italic text-ink">— Marella</p></Reveal>
        </div>
      </section>

      {/* WHAT YOU WILL LEARN */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28">
          <Reveal><MetaLabel className="mb-12">What you will learn</MetaLabel></Reveal>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-6 border-t border-line pt-10">
            {OUTCOMES.map((o, i) => (
              <Reveal key={o.t} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-5 py-4 border-b border-line/70">
                  <span className="font-serif text-accent text-lg mt-1">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="text-lg font-light text-ink">{o.t}</p>
                    <p className="mt-1.5 text-sm font-light text-ink-soft leading-relaxed" data-testid={`outcome-${i + 1}`}>{o.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16">
            <Reveal><p className="text-[11px] uppercase tracking-ultra text-accent mb-8">What's included</p></Reveal>
            <div className="border-t border-line" data-testid="whats-included">
              {INCLUDED.map((grp, i) => (
                <Reveal key={grp.label} delay={i * 0.05}>
                  <div className="py-6 border-b border-line grid grid-cols-12 gap-3 lg:gap-4">
                    <p className="col-span-12 lg:col-span-3 text-[11px] uppercase tracking-[0.2em] text-ink-soft pt-1">{grp.label}</p>
                    <ul className="col-span-12 lg:col-span-9 space-y-4">
                      {grp.items.map((it) => (
                        <li key={it.title}>
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span className="font-serif text-xl lg:text-2xl font-light text-ink">{it.title}</span>
                            {it.note && <span className="text-xs uppercase tracking-[0.15em] text-ink-soft">{it.note}</span>}
                          </div>
                          {it.d && <p className="mt-1 text-sm font-light text-ink-soft leading-relaxed max-w-xl">{it.d}</p>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.2em] text-ink" data-testid="files-included-count">7 files included</p>
              <p className="text-xs font-light text-ink-soft max-w-md text-right">The two workbooks are reusable professional systems built to travel with you from one household to the next — each household's information stays separate and confidential.</p>
            </div>
            <Reveal>
              <div className="mt-12 pt-8 border-t border-line flex flex-wrap items-center justify-between gap-6" data-testid="mid-cta">
                <p className="text-lg font-light text-ink-soft max-w-md">Your introduction to the profession, with practical tools to begin.</p>
                <div className="flex items-center gap-6">
                  <span className="font-serif text-2xl text-ink">${price}</span>
                  <CTA testid="mid-begin" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-20">
        <Reveal className="lg:col-span-4"><h2 className="font-serif font-light text-3xl lg:text-4xl text-ink tracking-tight">This may be for you if…</h2></Reveal>
        <div className="lg:col-span-8 max-w-2xl border-t border-line">
          {WHO_FOR.map((w, i) => (
            <Reveal key={i} delay={i * 0.05}><p className="py-5 border-b border-line text-lg font-light text-ink-soft leading-relaxed">{w}</p></Reveal>
          ))}
        </div>
      </section>

      {/* WHAT THIS IS NOT */}
      <section className="border-t border-line bg-ink text-bg">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <Reveal className="lg:col-span-4"><p className="text-[11px] uppercase tracking-ultra text-accent">In honesty</p><h2 className="mt-6 font-serif font-light text-3xl lg:text-4xl tracking-tight">What this course is not</h2></Reveal>
          <div className="lg:col-span-8 max-w-2xl space-y-5 text-lg font-light text-bg/75 leading-relaxed">
            {NOT.map((n, i) => <Reveal key={i} delay={i * 0.05}><p>{n}</p></Reveal>)}
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32 text-center border-b border-line">
        <Reveal>
          <p className="text-[11px] uppercase tracking-ultra text-accent">The Investment</p>
          <h2 className="mt-6 font-serif font-light text-3xl lg:text-5xl text-ink tracking-tight">How to Start as an Estate Manager</h2>
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-ink-soft">Self-paced online course</p>
          <p className="mt-8 font-serif text-5xl text-ink">${price}</p>
          <div className="mt-8 flex justify-center"><CTA testid="investment-begin" /></div>
          <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-ink-soft">Secure Stripe checkout · Activation email · Your library</p>
          {comingSoon ? (
            <p className="mt-4 text-xs font-light text-ink-soft" data-testid="checkout-soon-note">
              New enrollment is opening soon while this course is being refined. Existing members retain full access via their member area.
            </p>
          ) : (
            <p className="mt-4 text-xs font-light text-ink-soft" data-testid="checkout-legal-note">
              This is a digital course — all sales are final once access is granted. New customers receive a secure activation email after payment; existing customers sign in. Secure payment via Stripe. By purchasing, you agree to our{" "}
              <Link to="/terms" className="link-underline text-ink">Terms</Link> and{" "}
              <Link to="/refund-policy" className="link-underline text-ink">Refund Policy</Link>.
            </p>
          )}
        </Reveal>
      </section>

      {/* ABOUT MARELLA */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-4">
          <div className="overflow-hidden aspect-[4/5] max-w-sm"><img src={IMAGES.portrait} alt="Marella Bolognesi" className="w-full h-full object-cover object-top" /></div>
        </Reveal>
        <div className="lg:col-span-8 max-w-2xl">
          <Reveal><MetaLabel>About Marella</MetaLabel></Reveal>
          <Reveal delay={0.06}><p className="mt-6 font-serif font-light text-2xl lg:text-3xl text-ink leading-[1.3]">A working Estate Manager based in Miami, Florida.</p></Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-lg font-light text-ink-soft leading-relaxed">Marella has managed private residences in Miami for more than six years. Her background in hospitality management, international medical congresses and events, and interior design shapes how she runs a household — and how she teaches. Il Salotto Digitale is where she shares that practical experience with those entering the profession.</p></Reveal>
          <Reveal delay={0.14}><Link to="/about" data-testid="product-about-link" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">About Marella <ArrowUpRight size={15} /></Link></Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-12 py-20 lg:py-28">
          <Reveal><MetaLabel className="mb-12">Questions</MetaLabel></Reveal>
          <div className="border-t border-line">
            {FAQ.map((f, i) => (
              <div key={i} className="border-b border-line" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-6 text-left">
                  <span className="font-serif text-xl lg:text-2xl font-light text-ink">{f.q}</span>
                  {openFaq === i ? <Minus size={18} className="text-accent shrink-0" /> : <Plus size={18} className="text-accent shrink-0" />}
                </button>
                {openFaq === i && <p className="pb-6 -mt-1 text-lg font-light text-ink-soft leading-relaxed max-w-2xl">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-36 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl lg:text-6xl text-ink tracking-tight max-w-3xl mx-auto leading-[1.05]">Every career begins with understanding the work.</h2>
          <p className="mt-8 text-lg font-light text-ink-soft max-w-xl mx-auto leading-relaxed">If Estate Management is a profession you've been curious about, start by understanding what the work actually requires — and where you may fit within it.</p>
          <p className="mt-8 font-serif text-4xl text-ink">${price}</p>
          <div className="mt-8 flex justify-center"><CTA testid="final-begin" /></div>
        </Reveal>
      </section>
    </div>
  );
}
